import { createProductType } from '../../../../store/productTypes/create';

const normalizeProductTypeTitle = (title) => {
  if (!title) return '';
  return title.trim().replace(/\s+/g, ' ').toLowerCase();
};

/**
 * Find a registry item by case/whitespace-insensitive title match.
 * @param {Array<{_id: string, title: string}>} items
 * @param {string} title
 * @returns {{_id: string, title: string}|null}
 */
export const findProductTypeByNormalizedTitle = (items, title) => {
  const normalizedTitle = normalizeProductTypeTitle(title);
  if (!normalizedTitle) return null;

  return items.find(
    (productType) => normalizeProductTypeTitle(productType.title) === normalizedTitle,
  ) || null;
};

/**
 * Filter product types by a case-insensitive substring match against their title.
 * Purely client-side — the full registry is always loaded, no pagination/server search.
 * @param {Array<{_id: string, title: string}>} items
 * @param {string} query
 * @returns {Array<{_id: string, title: string}>}
 */
export const filterProductTypesByQuery = (items, query) => {
  const trimmedQuery = query.trim().toLowerCase();
  if (!trimmedQuery) return items;
  return items.filter((productType) => productType.title.toLowerCase().includes(trimmedQuery));
};

/**
 * Resolve the "checked" dropdown item(s) for the current field value.
 * - No value selected -> nothing checked.
 * - Value matches a registered product type -> that registry item is checked (case-insensitive).
 * - Value is a legacy/live title that isn't (yet) registered -> a synthetic placeholder item
 *   is checked so the dropdown still displays the current value.
 * @param {Array<{_id: string, title: string}>} items
 * @param {string} value
 * @returns {Array<{_id: string, title: string}>}
 */
export const getCheckedProductTypeItems = (items, value) => {
  if (!value) return [];

  const exactMatch = items.find((productType) => productType.title === value);
  const matchedProductType = exactMatch || findProductTypeByNormalizedTitle(items, value);
  if (matchedProductType) return [matchedProductType];

  return [{ _id: `__unregistered_product_type_${value}`, title: value }];
};

/**
 * Decide whether a `dispatch(createProductType(...))` result should be applied to the product's
 * `productType` field. A tolerated "already exists" (409) case resolves the thunk as fulfilled
 * just like a fresh create — the title is valid either way. A genuine failure (network/5xx/
 * permission) resolves the thunk as rejected and must never be silently applied.
 * @param {{type: string}} result - the action returned by `dispatch(createProductType(...))`
 * @returns {boolean}
 */
export const shouldApplyProductTypeCreateResult = (result) => createProductType.fulfilled.match(result);

export default filterProductTypesByQuery;
